import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { color } from '../tokens';
import { AccentButton, Button, Eyebrow, TextInput } from '../ui/primitives';

/**
 * Password and sessions.
 *
 * An account created through single sign-on has no password to confirm, so for
 * those this reads as "set a password" — which is how someone stops depending
 * on their identity provider being reachable to get in.
 */
export function SecuritySection({
  accent,
  onNotify,
  onSignedOut,
}: {
  accent: string;
  onNotify: (message: string) => void;
  onSignedOut: () => void;
}) {
  const user = useSession((s) => s.user);
  const setUser = useSession((s) => s.setUser);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hasPassword = user?.hasPassword ?? true;

  const reset = () => {
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
  };

  const submit = async () => {
    if (busy) return;
    setError(null);

    if (hasPassword && !current) {
      setError('Enter your current password.');
      return;
    }
    if (next.length < 10) {
      setError('New passwords need at least 10 characters.');
      return;
    }
    if (next !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { user: updated } = await api.changePassword(next, hasPassword ? current : undefined);
      setUser(updated);
      reset();
      setOpen(false);
      onNotify(hasPassword ? 'Password changed — other sessions signed out' : 'Password set');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  };

  const signOutEverywhere = async () => {
    if (!confirm_('This signs you out on every device, including this one. Continue?')) return;
    try {
      await api.logoutEverywhere();
      onSignedOut();
    } catch (err) {
      onNotify(err instanceof ApiError ? err.message : 'Could not sign out everywhere.');
    }
  };

  return (
    <section>
      <Eyebrow>Security</Eyebrow>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>{hasPassword ? 'Password' : 'No password set'}</div>
          <div style={{ fontSize: 12, color: color.textDim, marginTop: 2 }}>
            {hasPassword
              ? 'Changing it signs out your other devices.'
              : 'This account signs in with SSO. Add a password to sign in without it.'}
          </div>
        </div>
        <Button
          onClick={() => {
            reset();
            setOpen((was) => !was);
          }}
          hoverStyle={{ background: color.hover }}
          style={{
            height: 30,
            padding: '0 13px',
            borderRadius: 9,
            border: `1px solid ${color.lineStrong}`,
            background: 'transparent',
            color: color.text,
            fontSize: 12.5,
            fontWeight: 600,
            flex: 'none',
          }}
        >
          {open ? 'Cancel' : hasPassword ? 'Change' : 'Set password'}
        </Button>
      </div>

      {open && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 12 }}
        >
          {hasPassword && (
            <TextInput
              value={current}
              onChange={setCurrent}
              placeholder="Current password"
              type="password"
              name="currentPassword"
              autoComplete="current-password"
              autoFocus
            />
          )}
          <TextInput
            value={next}
            onChange={setNext}
            placeholder="New password — 10 characters or more"
            type="password"
            name="newPassword"
            autoComplete="new-password"
            autoFocus={!hasPassword}
          />
          <TextInput
            value={confirm}
            onChange={setConfirm}
            placeholder="Repeat it"
            type="password"
            name="confirmPassword"
            autoComplete="new-password"
          />

          {error && (
            <div role="alert" style={{ fontSize: 12.5, color: color.danger, lineHeight: 1.5 }}>
              {error}
            </div>
          )}

          <AccentButton accent={accent} type="submit" disabled={busy} style={{ alignSelf: 'flex-start' }}>
            {busy ? 'Saving…' : hasPassword ? 'Change password' : 'Set password'}
          </AccentButton>
        </form>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600 }}>Signed in elsewhere?</div>
          <div style={{ fontSize: 12, color: color.textDim, marginTop: 2 }}>
            Ends every session, on this device and any other.
          </div>
        </div>
        <Button
          onClick={() => void signOutEverywhere()}
          hoverStyle={{ background: 'rgba(248,113,113,.08)' }}
          style={{
            height: 30,
            padding: '0 13px',
            borderRadius: 9,
            border: `1px solid rgba(248,113,113,.35)`,
            background: 'transparent',
            color: color.danger,
            fontSize: 12.5,
            fontWeight: 600,
            flex: 'none',
          }}
        >
          Sign out everywhere
        </Button>
      </div>
    </section>
  );
}

/** Named so the local `confirm` state cannot shadow the global dialog. */
const confirm_ = (message: string): boolean => window.confirm(message);
